import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Heart, ArrowRight, Sparkles } from "lucide-react";

const STORAGE_KEY = "fr_welcome_disclaimer_v1";

/**
 * WelcomeDisclaimerModal — first-visit-only welcome note telling every Indian
 * family that the Family Repository is built for ALL of India's diversity.
 * Once closed, a localStorage flag stops it reappearing on that device.
 */
export function WelcomeDisclaimerModal() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            if (localStorage.getItem(STORAGE_KEY)) return undefined;
        } catch { return undefined; }
        const t = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(t);
    }, []);

    const close = () => {
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
        setOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
            <DialogContent
                className="max-w-2xl p-0 overflow-hidden gap-0 border-none rounded-2xl"
                data-testid="welcome-disclaimer-modal"
            >
                {/* Header band */}
                <div className="relative px-6 sm:px-8 pt-7 pb-6 bg-gradient-to-br from-[#2A1458] via-[#3B1E6E] to-[#1B5E20] text-white">
                    <div className="absolute -top-10 -right-6 h-40 w-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11.5px] font-semibold uppercase tracking-wide">
                        <Heart className="h-3.5 w-3.5 text-[#F9C784]" /> For Every Indian Family
                    </span>
                    <h2 className="mt-3 font-extrabold leading-tight" style={{ fontSize: "clamp(20px, 3vw, 28px)" }}>
                        One India. Thousands of Traditions.{" "}
                        <span className="text-[#F9C784]">One Family Repository.</span>
                    </h2>
                    <p className="mt-1.5 text-white/85 text-[14px] font-medium">
                        Created in India, by Indians, for every Indian family.
                    </p>
                </div>

                {/* Scrollable body */}
                <div className="px-6 sm:px-8 py-5 max-h-[50vh] overflow-y-auto text-[14px] leading-relaxed text-[#4B5563] space-y-3.5">
                    <p>
                        India is not one single culture—it is a magnificent tapestry of thousands of religions,
                        communities, ethnicities, languages, traditions, customs, rituals, festivals, beliefs and
                        family practices that have evolved over generations.
                    </p>
                    <p>
                        Our Family Repository has been thoughtfully designed keeping this extraordinary Indian
                        diversity at its heart. It is a comprehensive platform created to help families preserve,
                        document and pass on their family history, cultural heritage, religious practices, traditions,
                        rituals, customs, values, stories, memories and legacy to future generations.
                    </p>
                    <p>
                        The platform provides <strong className="text-[#1B5E20]">more than 1,000 areas and fields</strong>{" "}
                        for recording different aspects of family and cultural heritage. These cover a wide range of
                        traditions, rituals, ceremonies, festivals, food practices, family customs, ancestral
                        information, languages, photographs, stories, beliefs, important occasions and other elements
                        that make every family unique.
                    </p>
                    <div className="border-l-4 border-[#C9A55B] bg-[#FFF9ED] rounded-r-lg px-4 py-3">
                        <p className="font-semibold text-[#6D28D9]">
                            But India is too diverse to be completely captured by a predefined list.
                        </p>
                        <p className="text-[#92400E] mt-0.5">That is why we have built flexibility into the platform.</p>
                    </div>
                    <p>
                        If you do not find a particular field, tradition, ritual, practice, belief, family custom or
                        piece of cultural information that is unique to your community or family, you can simply{" "}
                        <strong className="text-[#1B5E20]">add your own information</strong>. The platform gives every
                        family the freedom to create and preserve information in its own words and in its own way.
                    </p>
                    <p>
                        Whether your family belongs to a particular religion, caste, community, ethnic group,
                        linguistic tradition or cultural heritage originating in India, your heritage deserves a place.
                    </p>
                    <div className="border-l-4 border-[#1B5E20] bg-[#F1F8F1] rounded-r-lg px-4 py-3">
                        <p className="text-[12px] font-semibold uppercase tracking-wide text-[#1B5E20]">Our objective is simple</p>
                        <p className="font-semibold text-[#1F2937] mt-0.5">
                            No Indian family should feel that its heritage is too unique to be preserved.
                        </p>
                    </div>
                    <p>
                        From the oldest family tradition to a newly created family practice, from ancestral memories to
                        contemporary family values, the Family Repository provides one comprehensive digital space where
                        your family's identity can be documented and preserved.
                    </p>
                    <p className="italic text-[#6D28D9] font-medium">
                        Because heritage should not disappear simply because nobody recorded it.
                    </p>
                    <p className="text-center font-bold text-[#1F2937] text-[15px] pt-1">
                        Your family. Your culture. Your traditions. Your story. Your way.
                    </p>
                    <p className="text-center">
                        One platform—designed to embrace the incredible diversity of India and help every Indian family
                        preserve what makes it unique.
                    </p>
                </div>

                {/* Footer */}
                <div className="px-6 sm:px-8 py-4 border-t border-[#EEEEEE] bg-white flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-[13px] font-semibold text-[#1B5E20] flex items-center gap-1.5">
                        <Sparkles className="h-4 w-4 text-[#C9A55B]" /> Let the next generation inherit more than memories.
                    </p>
                    <Button
                        onClick={close}
                        data-testid="welcome-disclaimer-cta"
                        className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white rounded-full px-6 shrink-0"
                    >
                        Enter Family Repository <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default WelcomeDisclaimerModal;
